import React from "react";
import { useLocation, useNavigate, useParams, useMatch } from "react-router-dom";

/**
 * A compatibility HOC that provides the same API as the withRouter HOC from React Router v5,
 * but uses the hooks from React Router v6.
 * 
 * @param {React.ComponentType} Component - The component to wrap
 * @returns {React.FC} - A wrapped component with router props
 */
export const withRouter = (Component) => {
  const WithRouterComponent = (props) => {
    // React Router v6 hooks
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    
    // Create a history object similar to v5's history
    const history = {
      push: (path, state) => navigate(path, { state }),
      replace: (path, state) => navigate(path, { replace: true, state }),
      go: (n) => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      location
    };
    
    // Create a match object similar to v5's match
    const match = {
      params,
      isExact: location.pathname === useMatch(location.pathname)?.pathname,
      path: useMatch(location.pathname)?.pattern?.path || location.pathname,
      url: location.pathname
    };
    
    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={match}
      />
    );
  };
  
  // Set displayName for better debugging
  const displayName = Component.displayName || Component.name || 'Component';
  WithRouterComponent.displayName = `withRouter(${displayName})`;
  
  return WithRouterComponent;
};